import { useExportAttendeesMutation } from "api/queries/attendees";
import type { ExportAttendeesRequest } from "api-client/controllers/forumAttendee";
import DropdownButton from "components/DropdownButton";
import { FileExportType } from "generated/api";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { downloadFileByURL } from "utils/fileUtils";

interface ExportAttendeesButtonProps {
  exportParams: Omit<ExportAttendeesRequest, "exportType" | "selectedAttendeeIds">;
  isFiltered: boolean;
  matchingAttendeesCount?: number;
  orderBy?: string;
  orderDir?: string;
  selectedAttendeeIds?: number[];
}

const ExportAttendeesButton: FC<ExportAttendeesButtonProps> = ({
  exportParams,
  isFiltered,
  matchingAttendeesCount = 0,
  orderBy,
  orderDir,
  selectedAttendeeIds = [],
}) => {
  const { t } = useTranslation();
  const hasSelectedAttendees = selectedAttendeeIds.length > 0;
  const btnTitle = t("Export");

  const exportAttendeesMutation = useExportAttendeesMutation({
    onSuccess: ({ data }) => downloadFileByURL(data),
    onError: () => toast.error(t("Something went wrong")),
  });

  const exportAttendees = (exportType: FileExportType) => {
    if (!hasSelectedAttendees && isFiltered && matchingAttendeesCount === 0) {
      toast.warn(t("No attendees to export"));
      return;
    }

    exportAttendeesMutation.mutate({
      ...exportParams,
      orderBy,
      orderDir,
      exportType,
      selectedAttendeeIds: hasSelectedAttendees ? selectedAttendeeIds : undefined,
    });
  };

  const items = [
    {
      label: t("Excel"),
      key: "excel",
      onClick: () => exportAttendees(FileExportType.Xls),
    },
    {
      label: t("CSV"),
      key: "csv",
      onClick: () => exportAttendees(FileExportType.Csv),
    },
  ];

  return <DropdownButton title={btnTitle} items={items} isLoading={exportAttendeesMutation.isLoading} />;
};

export default ExportAttendeesButton;
